import { InMemoryCache, IntrospectionFragmentMatcher, NormalizedCacheObject } from 'apollo-cache-inmemory';
import { ApolloClient } from 'apollo-client';
import { setContext } from 'apollo-link-context';
import { createHttpLink, HttpLink } from 'apollo-link-http';
import firebase from 'firebase/app';
import Vue from 'vue';
import VueApollo from 'vue-apollo';

import { handleIdentityProviderRequired } from '@/utils/identityProviderRequired';

import introspectionQueryResultData from './fragmentTypes.json';
import { MUT_IS_LOADING, MUT_LOGOUT, MUT_PARENT_AUTH_TOKEN, MUT_SNACKBAR, store } from './store';
import { isAuthEmbedded } from './utils/axiosWrapper';
import { notifyParentRefreshRequired, requestTokenFromParent } from './utils/iframeMessageRequester';
const fragmentMatcher = new IntrospectionFragmentMatcher({
  introspectionQueryResultData,
});
// Install the vue plugin
Vue.use(VueApollo);

const GRAPHQL_ENDPOINT_HTTP = process.env.VUE_APP_GRAPHQL_HTTP || 'http://localhost:4000/graphql';
const RPT_GRAPHQL_ENDPOINT_HTTP = process.env.VUE_APP_RPT_GRAPHQL_HTTP || 'http://localhost:4000/graphql';

const cache = new InMemoryCache({ fragmentMatcher });

const authLink = setContext(async (_, { headers }) => {
  // If in iframe, request fresh token from parent (same as axios)
  if (window.self !== window.top && !isAuthEmbedded()) {
    try {
      const parentToken = await requestTokenFromParent();
      store.commit(MUT_PARENT_AUTH_TOKEN, parentToken);
      const accessToken = parentToken || store.state.authTokens?.accessToken.token;

      if (accessToken) {
        return {
          headers: {
            ...headers,
            Authorization: `Bearer ${accessToken}`,
          },
        };
      }
    } catch (err) {
      console.error('Failed to get parent token, falling back to cookies:', err);
      // Fall through to cookie auth
    }
  }

  // Not in iframe or no token - use cookies
  return {
    withCredentials: true,
    ...headers,
  };
});

export function createClients(
  queryParams?: Record<string, string>,
  authToken?: string
): {
  clients: Record<string, ApolloClient<NormalizedCacheObject>>;
  default: ApolloClient<NormalizedCacheObject>;
} {
  let urlSuffix = '';
  if (queryParams && Object.values(queryParams).length) {
    urlSuffix = '?' + new URLSearchParams(queryParams);
  }

  const urls = { apolloClient: GRAPHQL_ENDPOINT_HTTP, rptApolloClient: RPT_GRAPHQL_ENDPOINT_HTTP };
  const headers: Record<string, string> = {
    'X-Requested-With': 'XMLHttpRequest',
  };
  if (authToken) {
    headers.Authorization = `Bearer ${authToken}`;
  }
  const clients = Object.entries(urls).reduce(
    (a, [name, url]) => ({
      [name]: new ApolloClient({
        link: authLink.concat(
          createHttpLink({
            uri: url + urlSuffix,
            credentials: 'include',
            headers,
          })
        ),
        cache,
      }),
      ...a,
    }),
    {} as Record<string, ApolloClient<NormalizedCacheObject>>
  );

  return { clients, default: clients.apolloClient };
}

const apolloProviderFactory = () => {
  const clients = createClients();
  return new VueApollo({
    clients: clients.clients,
    defaultClient: clients.default,
    errorHandler: async (err) => {
      const isAuthPage = window.location.hash.includes('auth');
      const error = JSON.parse(JSON.stringify(err, null, 2));
      const { networkError } = error;
      // Org identity provider policy refusal: a GraphQL error with the code in
      // extensions. api-svc answers it with a non-2xx status, which apollo-client
      // reports as a networkError carrying the body as `result` (graphQLErrors is
      // only populated for 200s), so look in both places. Hand off to the sign-in flow.
      if (
        handleIdentityProviderRequired(200, { errors: error.graphQLErrors ?? [] }, true) ||
        (networkError && handleIdentityProviderRequired(networkError.statusCode ?? 0, networkError.result, true))
      ) {
        return;
      }
      if (networkError) {
        const statusCode = networkError.statusCode;

        // Handle 401 - notify parent to reload entire site
        if (statusCode === 401 && !isAuthPage) {
          // If in iframe, notify parent to reload entire site
          if (window.self !== window.top && !isAuthEmbedded()) {
            console.log('🔄 Apollo 401 in iframe, notifying parent to reload...');
            notifyParentRefreshRequired();

            // Give parent time to receive and process the reload message
            // This prevents race conditions and potential data loss
            await new Promise((resolve) => setTimeout(resolve, 100));
            return;
          }

          // Not in iframe or embedded auth - redirect to login
          window.location.href = `${process.env.VUE_APP_BASE_URL}/#/auth`;
        } else if (
          (statusCode === 403 ||
            (statusCode === 400 && networkError.result?.errors?.[0]?.extensions?.code === 'UNAUTHENTICATED')) &&
          !isAuthPage
        ) {
          firebase.auth().signOut();
          store.commit(MUT_LOGOUT);
          store.commit(MUT_IS_LOADING, false);
          if (err.message) {
            store.commit(MUT_SNACKBAR, {
              color: 'error',
              message: err.message,
            });
          }
          window.location.href = `${process.env.VUE_APP_BASE_URL}/#/auth`;
        }
        console.error('apollo error', err.message, statusCode, networkError.result, err.stack);
      }
    },
  });
};

export const apolloProvider = apolloProviderFactory();
